<template>
  <router-view v-if="isHomePage" name="page"></router-view>

  <div
    v-else
    class="container-fluid fullheight redbook-app-root"
    :class="{ 'redbook-app-root--read': !showMap }"
  >
    <div
      class="redbook-page-column"
      :class="{ 'redbook-page-column--full': !showMap }"
    >
      <header
        ref="pageNav"
        class="redbook-page-nav"
        :class="{ 'redbook-page-nav--read': isReadPage }"
      >
        <router-link
          exact
          class="redbook-page-nav__link redbook-page-nav__link--home"
          :to="homeLink"
          aria-label="Главная страница"
          title="Главная страница"
        >
          <svg
            class="redbook-page-nav__icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M3 10.5L12 3l9 7.5" />
            <path d="M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5" />
            <path d="M9.5 21v-6.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V21" />
          </svg>
        </router-link>
        <router-link class="redbook-page-nav__link" :to="mapLink">
          Карта
        </router-link>
        <router-link class="redbook-page-nav__link" :to="readLink">
          Чтение
        </router-link>

        <div class="redbook-page-nav__end">
          <a
            v-if="isEditor && adminEditHref"
            class="redbook-admin-edit-link"
            :href="adminEditHref"
            aria-label="Edit in admin"
            title="Edit in admin"
          >
            <svg
              class="redbook-admin-edit-link__icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M8 3.5h8l4.5 4.5V20a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z"
              />
              <path d="M16 3.5V8h4.5" />
              <path d="M8 16.5l6.8-6.8 2.5 2.5-6.8 6.8L8 19z" />
              <path d="M14 10.5l2.5 2.5" />
            </svg>
          </a>
          <span v-if="isReadPage" class="redbook-page-nav__title">
            Красная Книга Архнадзора
          </span>
        </div>
      </header>
      <router-view name="page"></router-view>
    </div>
    <google-map v-if="showMap" :class="{ 'map-active': true }"></google-map>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { mapGetters } from 'vuex';
import GoogleMap from '@/components/GoogleMap.vue';

// import NavBar from '@/components/NavBar.vue';

import {
  FETCH_CURRENT_BUILDING,
  SHOW_PROBLEM_LAYERS_BY_IDS,
  SET_IS_MOBILE,
} from '@/store/actions.type';

export default {
  name: 'App',
  components: {
    // NavBar,
    GoogleMap,
  },
  data() {
    return {
      debouncedResizeHandler: null,
    };
  },
  computed: {
    ...mapGetters(['map', 'center', 'currentBuilding', 'isEditor']),
    showMap() {
      return !!this.$route.meta.showMap;
    },
    isHomePage() {
      return this.$route.name === 'home';
    },
    isReadPage() {
      return this.$route.name === 'read';
    },
    currentBuildingId() {
      const routeId = this.$route.params.id;
      if (routeId != null && `${routeId}`.trim() !== '') {
        return String(routeId);
      }

      if (this.currentBuilding?.id != null) {
        return String(this.currentBuilding.id);
      }

      return null;
    },
    adminEditHref() {
      const raw = this.currentBuildingId;
      if (raw == null || String(raw).trim() === '') return null;
      return `/edit/buildings?id=${encodeURIComponent(String(raw))}`;
    },
    homeLink() {
      return '/';
    },
    mapLink() {
      return this.currentBuildingId
        ? { name: 'map', params: { id: this.currentBuildingId } }
        : { name: 'map' };
    },
    readLink() {
      return this.currentBuildingId
        ? { name: 'read', params: { id: this.currentBuildingId } }
        : { name: 'read' };
    },
  },
  watch: {
    map(mapInstance) {
      if (mapInstance && this.showMap) {
        this.$nextTick(() => this.syncMapWithRoute(this.$route));
      }
    },
    $route() {
      this.syncCurrentBuilding(this.$route);
      if (this.showMap) {
        this.syncMapWithRoute(this.$route);
      }
      this.syncPageNavHeight();
    },
    isReadPage() {
      this.syncPageNavHeight();
    },
  },
  mounted() {
    this.syncCurrentBuilding(this.$route);

    if (this.map && this.showMap) {
      this.syncMapWithRoute(this.$route);
    }

    this.debouncedResizeHandler = debounce(this.handleResize, 100);
    window.addEventListener('resize', this.debouncedResizeHandler);
    this.syncPageNavHeight();
  },
  beforeDestroy() {
    if (this.debouncedResizeHandler) {
      window.removeEventListener('resize', this.debouncedResizeHandler);
    }
  },
  methods: {
    handleResize() {
      this.$store.commit(SET_IS_MOBILE, window.innerWidth <= 390);
      this.syncPageNavHeight();
    },
    syncPageNavHeight() {
      this.$nextTick(() => {
        const nav = this.$refs.pageNav;
        const height = nav ? nav.offsetHeight : 0;
        document.documentElement.style.setProperty(
          '--redbook-page-nav-height',
          `${height}px`
        );
      });
    },
    syncCurrentBuilding(route) {
      const id = route.params.id ?? null;
      this.$store.dispatch(FETCH_CURRENT_BUILDING, id);
    },
    syncMapWithRoute(route) {
      if (!this.map) {
        return;
      }

      const problemid = route.query.problemid;
      const id = route.params.id;
      const zoom = parseInt(route.query.zoom, 10);

      if (id) {
        this.$store.dispatch(FETCH_CURRENT_BUILDING, id).then((building) => {
          if (!this.map || !building) {
            return;
          }
          this.map.panTo({
            lat: parseFloat(building.lat),
            lng: parseFloat(building.lng),
          });
        });
      } else {
        this.map.panTo(this.center);
      }

      if (zoom === 1) {
        this.map.setZoom(17);
      } else {
        this.map.setZoom(13);
      }

      if (problemid) {
        this.$store.commit(SHOW_PROBLEM_LAYERS_BY_IDS, {
          visibility: true,
          problemId: problemid,
        });
      }
    },
  },
};
</script>

<style scoped>
.redbook-page-nav {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px;
  position: sticky;
  top: 0;
  z-index: 11;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #e5e7eb;
}

.redbook-page-nav__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 999px;
  color: #d2232a;
  text-decoration: none;
  font-weight: 600;
  line-height: 1;
}

.redbook-page-nav__link:not(.redbook-page-nav__link--home).router-link-active,
.redbook-page-nav__link:not(.redbook-page-nav__link--home).router-link-exact-active {
  background: #d2232a;
  color: #ffffff;
}

.redbook-page-nav__link--home,
.redbook-page-nav__link--home.router-link-active,
.redbook-page-nav__link--home.router-link-exact-active {
  background: transparent;
  color: inherit;
}

.redbook-page-nav__end {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.redbook-page-nav__title {
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.redbook-page-nav--read {
  background: #d2232a;
  border-bottom-color: #b81e24;
}

.redbook-page-nav--read .redbook-page-nav__link {
  background: transparent;
  color: #ffffff;
}

.redbook-page-nav--read
  .redbook-page-nav__link:not(.redbook-page-nav__link--home).router-link-active,
.redbook-page-nav--read
  .redbook-page-nav__link:not(.redbook-page-nav__link--home).router-link-exact-active {
  background: #ffffff;
  color: #d2232a;
}

.redbook-page-nav--read .redbook-page-nav__link--home .redbook-page-nav__icon {
  stroke: #ffffff;
}

.redbook-page-nav--read
  .redbook-page-nav__link:not(.redbook-page-nav__link--home).router-link-active
  .redbook-page-nav__icon,
.redbook-page-nav--read
  .redbook-page-nav__link:not(.redbook-page-nav__link--home).router-link-exact-active
  .redbook-page-nav__icon {
  stroke: #d2232a;
}

.redbook-page-nav--read
  .redbook-page-nav__link:not(.redbook-page-nav__link--home):hover:not(.router-link-active):not(
    .router-link-exact-active
  ) {
  background: rgba(255, 255, 255, 0.15);
}

.redbook-page-nav--read .redbook-page-nav__link--home:hover {
  background: rgba(255, 255, 255, 0.15);
}

.redbook-page-nav--read .redbook-admin-edit-link__icon {
  stroke: #ffffff;
}

.redbook-page-nav--read .redbook-admin-edit-link:hover .redbook-admin-edit-link__icon {
  stroke: #f3f4f6;
}

.redbook-page-nav__icon {
  display: block;
  width: 18px;
  height: 18px;
  stroke: #6b7280;
  stroke-width: 1.5;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.redbook-page-nav__link:hover .redbook-page-nav__icon {
  stroke: #4b5563;
}

.redbook-admin-edit-link__icon {
  display: block;
  width: 18px;
  height: 18px;
  stroke: #6b7280;
  stroke-width: 1.5;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.redbook-admin-edit-link:hover .redbook-admin-edit-link__icon {
  stroke: #4b5563;
}

.redbook-admin-edit-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.redbook-app-root--read {
  display: block;
}

.redbook-page-column--full {
  width: 100%;
}

/* #credits in redbook-v2.css is positioned for the map + sidebar layout (left: 427px).
   When the map is hidden for /read, reset so read UI stays in view. */
.redbook-app-root--read :deep(#credits) {
  position: relative;
  left: auto;
  top: auto;
  width: 100%;
  height: auto;
  min-height: 0;
  overflow: visible;
}

/* Same stylesheet forces white h1/h2 for the old hero over a dark cover; on white bg they vanish. */
.redbook-app-root--read :deep(#credits h1),
.redbook-app-root--read :deep(#credits h2) {
  color: #111827;
}
</style>
